import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FracturedHalo } from './FracturedHalo';
import { FloatingOrbs } from './FloatingOrbs';
export const WhyDeploid: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.6, 1.4, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.5, 0.8, 1], [0, 0.5, 1, 0.5, 0]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const titleScale = useTransform(scrollYProgress, [0, 0.3, 0.5], [0.8, 1.1, 1]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  // Minimal sunrise glow continuation
  const sunriseOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.05, 0.03, 0]);
  const authorityBlocks = [{
    heading: 'Architecture before implementation',
    subheading: 'Architecture-first philosophy',
    description: 'Most Salesforce partners implement requirements. We design the system your business runs on — ensuring scalability, clarity, and long-term evolution from day one.',
    accentColor: '#15c8ff'
  }, {
    heading: 'One partner. Complete ownership.',
    subheading: 'Full-stack ownership',
    description: 'From core clouds to integrations, analytics, and AI — we design, build, and evolve your entire Salesforce environment. No fragmented vendors. No disconnected layers.',
    accentColor: '#15c8ff'
  }, {
    heading: 'AI built into the system, not added later',
    subheading: 'Intelligence built in',
    description: 'We embed intelligence into workflows, decision-making, and operations — ensuring your Salesforce platform becomes progressively smarter over time.',
    accentColor: '#15c8ff'
  }, {
    heading: 'Your system improves continuously',
    subheading: 'Long-term evolution',
    description: 'Salesforce is not a one-off project. We continuously refine architecture, introduce new capabilities, and ensure your platform evolves alongside your business.',
    accentColor: '#15c8ff'
  }];
  return <>
      <section id="why-deploid" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden" data-id="element-384">
        {/* Floating Energy Orbs */}
        <FloatingOrbs count={12} data-id="element-385" />

        {/* Minimal sunrise glow continuation */}
        <motion.div className="absolute top-0 left-0 right-0 h-[30vh] pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250, 255, 49, 0.04) 0%, transparent 70%)',
        opacity: sunriseOpacity
      }} data-id="element-386" />

        {/* Bottom glow */}
        <motion.div className="absolute bottom-0 left-0 right-0 h-[50vh] pointer-events-none" style={{
        background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.10) 0%, transparent 60%)',
        opacity: sunriseOpacity
      }} data-id="element-387" />

        {/* Ambient yellow presence */}
        <div className="absolute inset-0 pointer-events-none opacity-15" data-id="element-388">
          <div className="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-electric-yellow/5 rounded-full blur-3xl" data-id="element-389" />
          <div className="absolute bottom-1/3 left-1/4 w-[400px] h-[400px] bg-electric-yellow/4 rounded-full blur-3xl" data-id="element-390" />
        </div>

        <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{
        scale,
        opacity,
        rotate
      }} data-id="element-391">
          <FracturedHalo size={900} segments={16} opacity={0.4} color="#4f0c9a" data-id="element-392" />
        </motion.div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-393">
          <motion.div className="text-center max-w-4xl mx-auto mb-20" style={{
          scale: titleScale,
          opacity: titleOpacity
        }} data-id="element-394">
            <motion.h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-wider mb-6 text-white leading-tight" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} data-id="element-395">
              Designed for{' '}
              <span className="text-electric-yellow" data-id="element-396">
                long-term system ownership
              </span>
            </motion.h2>
            <motion.p className="text-lg md:text-xl text-white/80 leading-relaxed" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-397">
              We architect, build, and evolve Salesforce as the operational
              foundation of your business — not as a one-off project, but as a
              system designed to scale, adapt, and improve over time.
            </motion.p>
          </motion.div>

          {/* Authority blocks - stacked vertically */}
          <div className="max-w-4xl mx-auto space-y-12" data-id="element-398">
            {authorityBlocks.map((block, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: index * 0.15,
            ease: 'easeOut'
          }} viewport={{
            once: true,
            amount: 0.3
          }} className="relative" data-id="element-399">
                {/* Thin divider line above (except first) */}
                {index > 0 && <motion.div className="absolute -top-6 left-0 right-0 h-[1px]" style={{
              background: `linear-gradient(to right, ${block.accentColor}40, transparent)`
            }} initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} transition={{
              duration: 0.8,
              delay: index * 0.15
            }} viewport={{
              once: true
            }} data-id="element-400" />}

                {/* Block content */}
                <div className="relative pl-6 border-l-2" style={{
              borderColor: `${block.accentColor}40`
            }} data-id="element-401">
                  {/* Subheading */}
                  <motion.div className="text-xs font-semibold tracking-wider uppercase mb-3" style={{
                color: block.accentColor
              }} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: index * 0.15 + 0.2
              }} viewport={{
                once: true
              }} data-id="element-402">
                    {block.subheading}
                  </motion.div>

                  {/* Main heading */}
                  <motion.h3 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-4 leading-tight" initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} transition={{
                duration: 0.6,
                delay: index * 0.15 + 0.3
              }} viewport={{
                once: true
              }} data-id="element-403">
                    {block.heading}
                  </motion.h3>

                  {/* Description */}
                  <motion.p className="text-base md:text-lg text-white/70 leading-relaxed" initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} transition={{
                duration: 0.6,
                delay: index * 0.15 + 0.4
              }} viewport={{
                once: true
              }} data-id="element-404">
                    {block.description}
                  </motion.p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final Trust Statement */}
      <section className="relative py-16 md:py-20 overflow-hidden" data-id="element-405">
        {/* Floating Energy Orbs */}
        <FloatingOrbs count={8} data-id="element-406" />

        {/* Glowing yellow bubble behind text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-407">
          <motion.div className="w-[800px] h-[400px] rounded-full" style={{
          background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(250, 255, 49, 0.15) 0%, rgba(250, 255, 49, 0.08) 30%, transparent 70%)',
          filter: 'blur(60px)'
        }} initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.2,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} data-id="element-408" />
        </div>

        {/* Subtle background accent */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-signal-indigo/5 to-transparent pointer-events-none" data-id="element-409" />

        {/* Final subtle sunrise glow */}
        <motion.div className="absolute top-0 left-0 right-0 h-[20vh] pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250, 255, 49, 0.02) 0%, transparent 70%)',
        opacity: 0.5
      }} data-id="element-410" />

        {/* Bottom glow for footer transition */}
        <motion.div className="absolute bottom-0 left-0 right-0 h-[30vh] pointer-events-none" style={{
        background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.06) 0%, transparent 60%)',
        opacity: 0.7
      }} data-id="element-411" />

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-412">
          <motion.div className="max-w-4xl mx-auto text-center" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-413">
            <motion.p className="text-lg md:text-xl lg:text-2xl text-white/80 leading-relaxed font-light" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-414">
              Trusted by organisations globally - from those beginning their
              digital transformation journey to established and scaling
              businesses - backed by{' '}
              <span className="text-electric-yellow font-medium" data-id="element-415">
                30+ years of collective Salesforce and AI delivery experience
              </span>
              .
            </motion.p>
          </motion.div>
        </div>
      </section>
    </>;
};